import React, { useState, useEffect } from 'react';
import { dartThemes } from '../dartThemes';
import { db } from '../firebase';
import { doc, updateDoc } from "firebase/firestore";

export const ProfilePage = ({ userRole, memberProfile, setLoggedInUser }) => {
    const [isEditing, setIsEditing] = useState(false);

    const inputClassName = 'input-field w-full';
    const inlineInputClassName = 'input-field w-full md:w-auto';

    // Member Personal Details
    const [nickname, setNickname] = useState(memberProfile?.nickname || '');

    // State for Dart Information
    const [dartBrand, setDartBrand] = useState(memberProfile?.dartInfo?.brand || '');
    const [dartRange, setDartRange] = useState(memberProfile?.dartInfo?.range || '');
    const [dartWeight, setDartWeight] = useState(memberProfile?.dartInfo?.weight || '');
    const [dartFlightShaft, setDartFlightShaft] = useState(memberProfile?.dartInfo?.flightShaft || '');
    const [dartPoints, setDartPoints] = useState(memberProfile?.dartInfo?.points || '');

    // State for Home Setup
    const [homeDartboard, setHomeDartboard] = useState(memberProfile?.homeSetup?.dartboard || '');
    const [homeLight, setHomeLight] = useState(memberProfile?.homeSetup?.light || '');
    const [homeSurround, setHomeSurround] = useState(memberProfile?.homeSetup?.surround || '');
    const [autoScoringSystemEnabled, setAutoScoringSystemEnabled] = useState(!!memberProfile?.homeSetup?.autoScoringSystem);
    const [homeAutoScoringSystem, setHomeAutoScoringSystem] = useState(memberProfile?.homeSetup?.autoScoringSystem || '');

    // State for Profile Image
    const [selectedDartTheme, setSelectedDartTheme] = useState(memberProfile?.profileImage || 'default');

    // Contact info
    const [phoneInput, setPhoneInput] = useState(memberProfile?.phoneNumber || '');
    const [phoneError, setPhoneError] = useState('');

    // Effect to update local state when memberProfile changes (e.g., on login)
    useEffect(() => {
        if (memberProfile) {
            setNickname(memberProfile.nickname || '');
            setDartBrand(memberProfile.dartInfo?.brand || '');
            setDartRange(memberProfile.dartInfo?.range || '');
            setDartWeight(memberProfile.dartInfo?.weight || '');
            setDartFlightShaft(memberProfile.dartInfo?.flightShaft || '');
            setDartPoints(memberProfile.dartInfo?.points || '');

            setHomeDartboard(memberProfile.homeSetup?.dartboard || '');
            setHomeLight(memberProfile.homeSetup?.light || '');
            setHomeSurround(memberProfile.homeSetup?.surround || '');
            setAutoScoringSystemEnabled(!!memberProfile.homeSetup?.autoScoringSystem); // Using !! to convert to boolean
            setHomeAutoScoringSystem(memberProfile.homeSetup?.autoScoringSystem || '');
            setSelectedDartTheme(memberProfile.profileImage || 'default');
            setPhoneInput(memberProfile.phoneNumber || '');
            setPhoneError('');
        }
    }, [memberProfile]);


    // Calculate tenure
    const calculateTenure = (joinDate) => {
        if (!joinDate) return "N/A";
        const now = new Date(); // Using ISO string date from Firestore
        const diffTime = Math.abs(now - joinDate);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays < 30) {
            return "Less than a month";
        }

        const years = Math.floor(diffDays / 365);
        const remainingDays = diffDays % 365;
        const months = Math.floor(remainingDays / 30); // Approximate months

        let tenureString = "";
        if (years > 0) {
            tenureString += `${years} year${years > 1 ? 's' : ''}`;
        }
        if (months > 0) {
            tenureString += `${years > 0 ? ' and ' : ''}${months} month${months > 1 ? 's' : ''}`;
        }
        return tenureString.trim();
    };

    const handleSave = async () => {
        if (!memberProfile?.uid) return; // Ensure we have a user ID

        const userDocRef = doc(db, "users", memberProfile.uid);

        // Validate and normalize phone number (store in E.164 when US-like)
        const normalizePhone = (input) => {
            if (!input) return '';
            const digits = ('' + input).replace(/\D/g, '');
            if (digits.length === 10) return `+1${digits}`; // US default
            if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`; // Already has US country code
            // Allow existing E.164 input
            if (/^\+\d{8,15}$/.test(input)) return input;
            return null; // invalid
        };

        const normalizedPhone = phoneInput ? normalizePhone(phoneInput) : '';
        if (phoneInput && !normalizedPhone) {
            setPhoneError('Please enter a valid phone number (e.g., 530-555-1234).');
            return; // Do not save on invalid
        }

        const updatedData = {
            nickname: nickname,
            profileImage: selectedDartTheme,
            dartInfo: {
                brand: dartBrand,
                range: dartRange,
                weight: dartWeight,
                flightShaft: dartFlightShaft,
                points: dartPoints,
            },
            homeSetup: {
                dartboard: homeDartboard,
                light: homeLight,
                surround: homeSurround,
                autoScoringSystem: autoScoringSystemEnabled ? homeAutoScoringSystem : '',
            },
            phoneNumber: normalizedPhone,
        };

        await updateDoc(userDocRef, updatedData);

        // Update local state to reflect changes immediately
        setLoggedInUser(prevUser => ({ ...prevUser, ...updatedData }));

        setIsEditing(false); // Exit edit mode
    };

    const handleToggleAutoScoring = () => {
        setAutoScoringSystemEnabled(prev => !prev);
        if (autoScoringSystemEnabled) { // If it was enabled and now toggling off
            setHomeAutoScoringSystem(''); // Clear the text field
        }
    };


    return (
        <div className="space-y-4">
            <h2 className="page-title heading-primary-1">Your Profile</h2>
            {userRole && memberProfile ? (
                <div className="space-y-6">
                    {/* Header with action */}
                    <div className="flex justify-between items-center">
                        <p className="text-xl font-semibold text-strong">Welcome, {memberProfile.nickname || memberProfile.firstName}!</p>
                        {(userRole === 'member' || userRole === 'admin') && (
                            <button
                                onClick={() => isEditing ? handleSave() : setIsEditing(true)}
                                className={`btn ${isEditing ? 'btn-success' : 'btn-primary'}`}
                            >
                                {isEditing ? 'Save Profile' : 'Edit Profile'}
                            </button>
                        )}
                    </div>

                    {/* Member Details Card */}
                    <div className="card rounded-lg p-6">
                        <h3 className="text-2xl font-semibold heading-primary-1 mb-4">Member Details</h3>
                        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                            <div>
                                <dt className="label">Name</dt>
                                <dd className="text-sm text-strong">{memberProfile.firstName} {memberProfile.lastName}</dd>
                            </div>
                            <div>
                                <dt className="label">Email</dt>
                                <dd className="text-sm text-strong">{memberProfile.email}</dd>
                            </div>
                            <div>
                                <dt className="label">Nickname</dt>
                                <dd className="text-sm">
                                    {isEditing ? (
                                        <input type="text" value={nickname} onChange={(e) => setNickname(e.target.value)}
                                               className={inputClassName} />
                                    ) : (
                                        <span className="text-strong">{nickname || '—'}</span>
                                    )}
                                </dd>
                            </div>
                            <div>
                                <dt className="label">Phone</dt>
                                <dd className="text-sm">
                                    {isEditing ? (
                                        <>
                                            <input
                                                type="tel"
                                                value={phoneInput}
                                                onChange={(e) => { setPhoneInput(e.target.value); setPhoneError(''); }}
                                                placeholder="530-555-1234"
                                                className={inputClassName}
                                            />
                                            {phoneError && <p className="mt-1 text-xs text-danger">{phoneError}</p>}
                                        </>
                                    ) : (
                                        <span className="text-strong">{memberProfile.phoneNumber ? memberProfile.phoneNumber.replace(/^\+1(\d{3})(\d{3})(\d{4})$/, '($1) $2-$3') : 'N/A'}</span>
                                    )}
                                </dd>
                            </div>
                            {/* Date Joined and Last Login removed per request */}
                            <div>
                                <dt className="label">Tenure</dt>
                                <dd className="text-sm text-strong">{calculateTenure(memberProfile.dateJoined)}</dd>
                            </div>
                        </dl>
                    </div>

                    {/* Profile Theme Card */}
                    <div className="card rounded-lg p-6">
                        <h3 className="text-2xl font-semibold heading-primary-1 mb-4">Profile Theme</h3>
                        <div className="flex items-center gap-4">
                            <div className="surface-low w-20 h-20 rounded-full flex items-center justify-center overflow-hidden shadow-inner">
                                {dartThemes[selectedDartTheme] || dartThemes['default']}
                            </div>
                            {isEditing && (
                                <div className="flex flex-wrap gap-2">
                                    {Object.keys(dartThemes).map((themeKey) => (
                                        <button
                                            key={themeKey}
                                            type="button"
                                            onClick={() => setSelectedDartTheme(themeKey)}
                                            className={`theme-option-btn w-12 h-12 rounded-full flex items-center justify-center p-1 transition-all duration-200 ${selectedDartTheme === themeKey ? 'theme-option-btn-selected' : ''}`}
                                        >
                                            {dartThemes[themeKey]}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Dart Information Card */}
                    <div className="card rounded-lg p-6">
                        <h3 className="text-2xl font-semibold heading-primary-1 mb-4">Dart Information</h3>
                        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                            <div>
                                <dt className="label">Brand</dt>
                                <dd className="text-sm">
                                    {isEditing ? (
                                        <input type="text" value={dartBrand} onChange={(e) => setDartBrand(e.target.value)} className={inputClassName} />
                                    ) : (
                                        <span className="text-strong">{dartBrand || '—'}</span>
                                    )}
                                </dd>
                            </div>
                            <div>
                                <dt className="label">Range</dt>
                                <dd className="text-sm">
                                    {isEditing ? (
                                        <input type="text" value={dartRange} onChange={(e) => setDartRange(e.target.value)} className={inputClassName} />
                                    ) : (
                                        <span className="text-strong">{dartRange || '—'}</span>
                                    )}
                                </dd>
                            </div>
                            <div>
                                <dt className="label">Weight</dt>
                                <dd className="text-sm">
                                    {isEditing ? (
                                        <input type="text" value={dartWeight} onChange={(e) => setDartWeight(e.target.value)} className={inputClassName} />
                                    ) : (
                                        <span className="text-strong">{dartWeight || '—'}</span>
                                    )}
                                </dd>
                            </div>
                            <div>
                                <dt className="label">Flight/Shaft</dt>
                                <dd className="text-sm">
                                    {isEditing ? (
                                        <input type="text" value={dartFlightShaft} onChange={(e) => setDartFlightShaft(e.target.value)} className={inputClassName} />
                                    ) : (
                                        <span className="text-strong">{dartFlightShaft || '—'}</span>
                                    )}
                                </dd>
                            </div>
                            <div>
                                <dt className="label">Points</dt>
                                <dd className="text-sm">
                                    {isEditing ? (
                                        <input type="text" value={dartPoints} onChange={(e) => setDartPoints(e.target.value)} className={inputClassName} />
                                    ) : (
                                        <span className="text-strong">{dartPoints || '—'}</span>
                                    )}
                                </dd>
                            </div>
                        </dl>
                    </div>

                    {/* Home Setup Card */}
                    <div className="card rounded-lg p-6">
                        <h3 className="text-2xl font-semibold heading-primary-1 mb-4">Home Setup Information</h3>
                        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                            <div>
                                <dt className="label">Dartboard</dt>
                                <dd className="text-sm">
                                    {isEditing ? (
                                        <input type="text" value={homeDartboard} onChange={(e) => setHomeDartboard(e.target.value)} className={inputClassName} />
                                    ) : (
                                        <span className="text-strong">{homeDartboard || '—'}</span>
                                    )}
                                </dd>
                            </div>
                            <div>
                                <dt className="label">Light</dt>
                                <dd className="text-sm">
                                    {isEditing ? (
                                        <input type="text" value={homeLight} onChange={(e) => setHomeLight(e.target.value)} className={inputClassName} />
                                    ) : (
                                        <span className="text-strong">{homeLight || '—'}</span>
                                    )}
                                </dd>
                            </div>
                            <div>
                                <dt className="label">Surround</dt>
                                <dd className="text-sm">
                                    {isEditing ? (
                                        <input type="text" value={homeSurround} onChange={(e) => setHomeSurround(e.target.value)} className={inputClassName} />
                                    ) : (
                                        <span className="text-strong">{homeSurround || '—'}</span>
                                    )}
                                </dd>
                            </div>
                            <div>
                                <dt className="label">Auto Scoring System</dt>
                                <dd className="text-sm">
                                    {isEditing ? (
                                        <div className="flex items-center gap-2">
                                            <button onClick={handleToggleAutoScoring} type="button" className={`btn py-1 px-3 text-sm ${autoScoringSystemEnabled ? 'btn-danger' : 'btn-success'}`}>{autoScoringSystemEnabled ? 'Disable' : 'Enable'}</button>
                                            {autoScoringSystemEnabled && (
                                                <input type="text" value={homeAutoScoringSystem} onChange={(e) => setHomeAutoScoringSystem(e.target.value)} className={inlineInputClassName} placeholder="System Name" />
                                            )}
                                        </div>
                                    ) : (
                                        <span className="text-strong">{homeAutoScoringSystem || '—'}</span>
                                    )}
                                </dd>
                            </div>
                        </dl>
                    </div>

                    {/* Teams Cards */}
                    {memberProfile.currentTeam && (
                        <div className="card rounded-lg p-6">
                            <h3 className="text-2xl font-semibold heading-primary-1">Current Team</h3>
                            <p className="mt-2 text-strong">{memberProfile.currentTeam.name}</p>
                        </div>
                    )}
                    {(memberProfile.formerTeams || []).length > 0 && (
                        <div className="card rounded-lg p-6">
                            <h3 className="text-2xl font-semibold heading-primary-1 mb-3">Former Teams</h3>
                            <ul className="space-y-2 text-strong">
                                {(memberProfile.formerTeams || []).map((team, index) => (
                                    <li key={index} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                                        <span className="font-medium">{team.name}</span>
                                        <span className="text-sm text-muted">Members: {team.members.join(', ')}</span>
                                        <span className="text-sm text-muted">Seasons: {team.seasonsPlayed}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            ) : (
                <p className="text-xl text-center text-danger">Please log in to view your profile.</p>
            )}
        </div>
    );
};